import type { ListedConnector } from "../../../api/connectorsApi";
import type { RouteUnavailability } from "../../../api/connectorStatus";
import { pcConnectorsOf } from "../setup/setupProgress";

/**
 * One card per provider on the person's PCs (F49, as T3 Code shows its providers): each agent
 * and each API route the connectors reported, whether it works and how to connect it. The
 * names, statuses and models are what the connectors reported (CN-4); nothing is assumed.
 */

/** How a provider stands on one PC. */
export type ProviderState = "ready" | "needs_sign_in" | "needs_key" | "not_installed" | "not_working" | "offline";

export interface ProviderOnPc {
  readonly connectorId: string;
  readonly pcLabel: string;
  readonly state: ProviderState;
  /** Why it does not work there, as the connector says. */
  readonly problem: string | null;
  /** The PC is online and runs its own page, where an agent is signed in or set up. */
  readonly hasPage: boolean;
}

export interface ProviderCard {
  /** `agent:<id>` or `api_key:<id>`, as capability facts key routes. */
  readonly key: string;
  readonly kind: "agent" | "api_key";
  readonly routeId: string;
  /** What the agent calls itself, else its ID on the PC; an API route's own id. */
  readonly name: string;
  /** An API route's provider, as the PC reported it: its API kind and host (DR-C-08); `null` for an agent. */
  readonly provider: { readonly api: string | null; readonly host: string } | null;
  /**
   * Its state on every PC, or `null` when its PCs differ (F86): ready on one PC never stands
   * for the card while another needs a sign-in. Each PC then says its own.
   */
  readonly state: ProviderState | null;
  /** The most models one PC offers through it. */
  readonly models: number;
  readonly pcs: readonly ProviderOnPc[];
  /**
   * The online PCs where it needs repair (OW-05): a sign-in, a set-up or a key. The repair
   * belongs to each of those PCs; the card names them.
   */
  readonly repairOn: readonly ProviderOnPc[];
}

/** An agent's card, from the server's answer (CP-11). */
function agentState(because: RouteUnavailability | null): ProviderState {
  return because === null ? "ready" : because === "not_logged_in" ? "needs_sign_in" : because === "not_installed" ? "not_installed" : "not_working";
}

interface Sighting {
  readonly card: Pick<ProviderCard, "key" | "kind" | "routeId" | "name" | "provider">;
  readonly pc: ProviderOnPc;
  readonly models: number;
}

function sightingsOf(connector: ListedConnector): Sighting[] {
  const { status } = connector;
  const onPc = (state: ProviderState, problem: string | null, hasPage: boolean): ProviderOnPc => ({
    connectorId: connector.connectorId,
    pcLabel: connector.name,
    state: status.online || state === "not_installed" ? state : "offline",
    problem,
    hasPage: hasPage && status.online,
  });
  const pageOnPc = status.approvalPageUrl !== null;
  return [
    ...status.agents.map((agent) => ({
      card: { key: `agent:${agent.agentId}`, kind: "agent" as const, routeId: agent.agentId, name: agent.title ?? agent.agentId, provider: null },
      pc: onPc(agentState(agent.unavailableBecause), agent.unavailableBecause === null ? null : agent.problem, pageOnPc),
      models: agent.models.length,
    })),
    ...status.apiRoutes.map((route) => {
      // The server's answer (CP-11): a missing or refused key needs a key; anything else doesn't work.
      const because = route.unavailableBecause;
      const state: ProviderState = because === null ? "ready" : because === "no_key" || because === "blocked" ? "needs_key" : "not_working";
      return {
        card: { key: `api_key:${route.routeId}`, kind: "api_key" as const, routeId: route.routeId, name: route.routeId, provider: { api: route.api, host: route.baseUrlHost } },
        // Its key is added on the PC's own page too (DR-C-08).
        pc: onPc(state, state === "ready" ? null : route.problem, pageOnPc),
        models: route.models.length,
      };
    }),
  ];
}

/** The cards for the person's active PC connectors, agents first, each group by name. */
export function providerCardsOf(connectors: readonly ListedConnector[]): ProviderCard[] {
  const byKey = new Map<string, [Sighting, ...Sighting[]]>();
  for (const sighting of pcConnectorsOf(connectors).flatMap(sightingsOf)) {
    const known = byKey.get(sighting.card.key);
    byKey.set(sighting.card.key, known === undefined ? [sighting] : [...known, sighting]);
  }
  const cards = [...byKey.values()].map(([first, ...more]): ProviderCard => {
    const sightings = [first, ...more];
    const pcs = sightings.map((sighting) => sighting.pc);
    return {
      ...first.card,
      state: pcs.every((onPc) => onPc.state === first.pc.state) ? first.pc.state : null,
      models: Math.max(...sightings.map((sighting) => sighting.models)),
      pcs,
      repairOn: pcs.filter((onPc) => onPc.state !== "ready" && onPc.state !== "offline"),
    };
  });
  return cards.sort((left, right) => (left.kind === right.kind ? left.name.localeCompare(right.name) : left.kind === "agent" ? -1 : 1));
}
